import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { User, UserUpdateInput, DashboardStats } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<UserUpdateInput>) => Promise<User>;
  refreshUser: () => Promise<User>;
  isAuthenticated: () => boolean;
  getUserStats: () => Promise<DashboardStats>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Failed to get user:', error);
          localStorage.removeItem('token');
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (token: string): Promise<void> => {
    try {
      setLoading(true);
      localStorage.setItem('token', token);
      
      const userData = await authService.getCurrentUser();
      setUser(userData);
      
      toast.success('Successfully logged in!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      localStorage.removeItem('token');
      toast.error('Login failed. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      navigate('/login', { replace: true });
      toast.success('Successfully logged out!');
    }
  };

  // Update user data
  const updateUser = async (userData: Partial<UserUpdateInput>): Promise<User> => {
    try {
      const updatedUser = await authService.updateUser(userData);
      setUser(updatedUser);
      toast.success('Profile updated successfully!');
      return updatedUser;
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  };

  // Refresh user data
  const refreshUser = async (): Promise<User> => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = (): boolean => {
    return !!user && !!localStorage.getItem('token');
  };

  // Get user stats
  const getUserStats = async (): Promise<DashboardStats> => {
    try {
      const stats = await authService.getUserStats();
      return stats;
    } catch (error) {
      console.error('Failed to get user stats:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated,
    getUserStats
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 